import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/article';
import type { Article } from '@/mocks/article';

const siteUrl = import.meta.env.VITE_SITE_URL;

const buildArticleSchema = (article: Article) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  author: {
    '@type': 'Organization',
    name: article.author,
  },
  datePublished: article.date,
  dateModified: article.date,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/artykul/${article.slug}`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Kakobuy Polska',
  },
});

const buildBreadcrumbSchema = (article: Article) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Strona główna',
      item: `${siteUrl}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: article.title,
      item: `${siteUrl}/artykul/${article.slug}`,
    },
  ],
});

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Artykuł nie został znaleziony
            </h1>
            <p className="text-foreground-500 mb-8">Przepraszamy, ale szukany artykuł nie istnieje.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-6 py-3 rounded-full transition-all duration-200 whitespace-nowrap"
            >
              Powrót na stronę główną
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = buildArticleSchema(article);
  const breadcrumbSchema = buildBreadcrumbSchema(article);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-16">
          <article className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8 flex-wrap" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200">
                Strona główna
              </Link>
              <span>/</span>
              <span className="text-foreground-600">Artykuł</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block bg-primary-100 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {article.description}
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-400">
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </header>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-16 p-6 md:p-8 bg-primary-50 rounded-2xl border border-primary-200/50">
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                Chcesz zobaczyć Kakobuy Spreadsheet?
              </h2>
              <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                Sprawdź naszą kompletną listę produktów i znajdź najlepsze okazje z Chin. Wszystkie produkty zweryfikowane, ze zdjęciami QC i aktualnymi cenami.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm px-6 py-3 rounded-full transition-all duration-200 whitespace-nowrap"
                >
                  Kakobuy Spreadsheet
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </div>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-background-100 text-foreground-700 font-semibold text-sm px-6 py-3 rounded-full border border-background-300 transition-all duration-200 whitespace-nowrap"
                >
                  Powrót na stronę główną
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </div>
                </Link>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                Więcej poradników Kakobuy
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/artykul/${a.slug}`}
                      className="flex items-center gap-3 p-3 rounded-lg border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/50 transition-all duration-200"
                    >
                      <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
                        <img src={a.imageUrl} alt={a.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground-800 truncate">{a.title}</p>
                        <p className="text-xs text-foreground-400">{a.readTime}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>

            <div className="mt-8">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors duration-200"
              >
                ← Powrót na stronę główną Kakobuy Polska
              </Link>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}